import React, { useCallback, useEffect, useState } from "react"
import { EarningCategory } from "../../types/wallet/WalletTypes";
import AddPayoutAccount from "../../components/wallet/AddPayoutAccount";
import { Link, useNavigate } from "react-router-dom";
import Tabs, { TabButton } from "../../components/tabs/Tabs";
import Payout from "../../components/wallet/Payout";
import WalletSkeleton from "../../skeletons/WalletSkeleton";
import { useDispatch } from "react-redux";
import { AppDispatch, RootState } from "../../redux-stores/store";
import { fetchWalletData } from "../../redux-stores/slices/walletSlice";
import { useSelector } from "react-redux";
import LinkPANModal from "../../components/wallet/LinkPANModal";
import { fetchPANDetailsThunk } from "../../redux-stores/slices/influencerSlice";
import { setHeader } from "../../redux-stores/slices/layoutSlice";
import { defaultAppHeaderProps } from "../../types/layout/layoutTypes";
import "./style.scss"

const Wallet: React.FC = () => {

    const dispatch: AppDispatch = useDispatch();
    const navigate = useNavigate();
    const walletData = useSelector((state: RootState) => state.wallet);
    const panDetails = useSelector((state: RootState) => state.influencer.pan);

    const [canWithdraw] = useState<boolean>(true);
    const [amount, setAmount] = useState<number>(0);

    const [payoutAccountModalVisible, setPayoutAccountModalVisibility] = useState<boolean>(false);
    const [payoutModalVisible, setPayoutModalVisible] = useState<boolean>(false);
    const [linkPANModalVisible, setLinkPANModalVisiblity] = useState<boolean>(false);

    const [isPayoutProcessing, setPayoutProcessing] = useState<boolean>(false);
    const [activeTab, setActiveTab] = useState<number>(0);

    const getWalletData = useCallback(async (force = false) => {
        dispatch(fetchWalletData(force));
    }, [dispatch]);

    useEffect(() => {
        setAmount(walletData.statistics?.balance ?? 0);
    }, [walletData])

    const withdraw = async () => {
        if (amount < 1) return;
        setPayoutModalVisible(true);
    }

    const addPayoutAccount = () => {
        setPayoutAccountModalVisibility(true);
    }

    useEffect(() => {
        if (!walletData.pan.available && walletData.pan.required) {
            dispatch(fetchPANDetailsThunk());
        }
    }, [walletData.pan])

    useEffect(() => {
        dispatch(setHeader({
            generic: false,
            page: {
                title: "Payments",
                onBackClick: () => navigate(-1)
            }
        }));
        return (() => {
            dispatch(setHeader(defaultAppHeaderProps))
        });
    }, [])


    const getTransactionTabs = (): TabButton[] => {
        let buttons: TabButton[] = [
            {
                id: 'earnings',
                content: "Credited"
            },
            {
                id: 'withdrawals',
                content: "Withdrawals"
            }
        ];


        return buttons
    }

    useEffect(() => {
        getWalletData();
    }, [getWalletData])

    if (walletData.loading) return <WalletSkeleton />

    return (
        <>
            <div className="app-page">
                <div className="app-card">
                    <div className="d-flex flex-column gap-4">
                        <div className="d-flex flex-column gap-2">
                            <h6 className="app-card-title">Available balance to withdraw</h6>
                            <span className="text-success wallet-amount">₹{walletData?.statistics?.balance}</span>
                        </div>
                        {
                            walletData?.payout_account && canWithdraw &&
                            <>
                                {
                                    (!walletData.pan.available && walletData.pan.required) ? (
                                        <button className="btn btn-primary" onClick={() => setLinkPANModalVisiblity(true)}>Add PAN</button>
                                    ) : (
                                        <button className="btn btn-primary" disabled={amount < 1} onClick={withdraw}>Withdraw</button>
                                    )
                                }
                            </>
                        }
                        {
                            !walletData?.payout_account && canWithdraw &&
                            <button className="btn btn-primary" onClick={addPayoutAccount}>Add Bank Details to withdraw</button>
                        }
                    </div>
                </div>
                <div className="app-card mt-4">
                    <div className="d-flex flex-column gap-075">
                        <div className="d-flex flex-column gap-2">
                            <h6 className="app-card-title">Total Money Earned</h6>
                            <span className="text-dark wallet-amount">₹{walletData?.statistics?.earnings}</span>
                        </div>
                        <div className="d-flex flex-column gap-075">
                            {
                                walletData?.statistics?.category_earnings &&
                                Object.entries(walletData.statistics?.category_earnings).map(([key, categoryEarning], i) => (
                                    <div key={i} className="app-sub-card wallet-earning-type-card">
                                        <div className="d-flex flex-column">
                                            <span className="text-dark fs-125 fw-600">₹{categoryEarning.earnings}</span>
                                            <div className="d-flex align-items-center justify-content-between">
                                                <span className="text-muted fs-075 fw-400">{categoryEarning.name} Earnings</span>
                                                {key === EarningCategory.REFERRAL && <Link className="fs-1 text-primary fw-600" to="/referral">Refer Now</Link>}
                                            </div>
                                        </div>
                                    </div>
                                ))
                            }
                        </div>
                    </div>
                </div>
                <div className="mt-32">
                    <h6 className="fs-24 fw-600 text-dark">Transaction History</h6>
                    {
                        (walletData?.transactions.length === 0) &&
                        <div className="app-card mt-3">
                            <div className="d-flex flex-column align-items-center gap-4 py-5 justify-content-center">
                                <div className="d-flex flex-column gap-075 text-center">
                                    <h3 className="fs-18 fw-600 text-dark">Collaborate with Top Brands and Start Earning Today</h3>
                                    <p className="text-dark-gray fs-14">Partner with leading brands to turn your creativity into income. Build meaningful collaborations and grow your earning opportunities effortlessly </p>
                                </div>
                                <Link className="btn btn-primary w-100" to="/">Find Collabs</Link>
                            </div>
                        </div>
                    }
                    {
                        walletData?.transactions && walletData?.transactions.length > 0 &&
                        <div className="app-card mt-3 p-0">
                            <div className="app-nav px-3 border-bottom-1">
                                <div className={`app-nav-item fs-12 ${activeTab === 0 ? 'text-dark fw-500 active' : 'text-muted fw-400'}`} onClick={() => setActiveTab(0)}>CREDITED</div>
                                <div className={`app-nav-item fs-12 ${activeTab === 1 ? 'text-dark fw-500 active' : 'text-muted fw-400'}`} onClick={() => setActiveTab(1)}>WITHDRAWAL</div>
                            </div>
                            <div className="p-3">
                                {activeTab === 0 && (
                                    <div className="d-flex flex-column gap-3">
                                        {
                                            walletData?.transactions.map((transaction, index) => (
                                                <div key={index} className="p-12 app-sub-card bg-light-gray d-flex align-items-center gap-3 justify-content-between">
                                                    <div className="d-flex flex-column gap-025">
                                                        <span className="text-dark-gray fw-400 fs-12">{transaction.posted}</span>
                                                        <span className={`fs-12 fw-400 ${transaction.dr_amount > 0 ? 'text-danger' : 'text-success'}`}>{transaction.narration}</span>
                                                    </div>
                                                    <span className={`fs-16 fw-500 ${transaction.dr_amount > 0 ? 'text-dark' : 'text-success'}`}>{transaction.dr_amount > 0 ? `(-) ₹${transaction.dr_amount}` : `₹${transaction.cr_amount}`}</span>
                                                </div>
                                            ))
                                        }
                                    </div>
                                )}
                                {
                                    activeTab === 1 && (
                                        <div className="d-flex flex-column gap-3">
                                            {
                                                walletData?.payouts.map((payout, index) => (
                                                    <div key={index} className="p-12 app-sub-card bg-light-gray d-flex align-items-center gap-3 justify-content-between">
                                                        <div className="d-flex flex-column gap-025">
                                                            <span className="text-dark-gray fw-400 fs-12">{payout.posted}</span>
                                                            {
                                                                payout.status === 'processed' &&
                                                                <span className={`fs-12 fw-400 text-success`}>Payout Successful</span>
                                                            }
                                                            {
                                                                payout.status === 'queued' &&
                                                                <span className={`fs-12 fw-400 text-warning`}>Payout Processing</span>
                                                            }
                                                            {
                                                                payout.status === 'failed' &&
                                                                <span className={`fs-12 fw-400 text-danger`}>Payout Failed</span>
                                                            }
                                                        </div>
                                                        <span className={`fs-16 fw-500 ${payout.status === 'processed' ? 'text-success' : 'text-dark'}`}>₹{payout.amount}</span>
                                                    </div>
                                                ))
                                            }
                                        </div>
                                    )
                                }
                            </div>
                        </div>
                    }
                </div>
            </div>
            <AddPayoutAccount
                isActive={payoutAccountModalVisible}
                onClose={() => setPayoutAccountModalVisibility(false)}
                onSuccess={() => {
                    getWalletData(true)
                    setPayoutAccountModalVisibility(false);
                }}
            />
            <LinkPANModal
                isActive={linkPANModalVisible}
                onClose={() => setLinkPANModalVisiblity(false)}
            />
            <Payout
                isActive={payoutModalVisible}
                onClose={() => {
                    setPayoutModalVisible(false)
                    getWalletData(true);
                }}
                onConfirmation={(is_processing) => setPayoutProcessing(is_processing)}
                bankAccountNumber={walletData?.payout_account?.bank_account_number ?? ''}
                amount={walletData?.statistics?.balance!}
                backdropClickDisabled={isPayoutProcessing}
            />
        </>

    )
}

export default Wallet;